<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import agentLogsAPI from 'dashboard/api/aifocus/agentLogs';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

// State
const logs = ref([]);
const stats = ref({
  total_today: 0,
  success_count: 0,
  error_count: 0,
  avg_execution_time: null,
  total_tokens: 0,
});
const isLoading = ref(true);
const isStreaming = ref(false);
const lastPollTime = ref(null);
const selectedLevel = ref('');
const selectedEventType = ref('');
const expandedLogs = ref(new Set());
const clearDialogRef = ref(null);
const isClearing = ref(false);

// Stream control
let streamTimeout = null;

// Log levels for filter
const levelOptions = [
  { value: '', label: 'Todos os niveis' },
  { value: 'debug', label: 'Debug' },
  { value: 'info', label: 'Info' },
  { value: 'warn', label: 'Aviso' },
  { value: 'error', label: 'Erro' },
  { value: 'success', label: 'Sucesso' },
];

// Event types for filter
const eventTypeOptions = [
  { value: '', label: 'Todos os eventos' },
  { value: 'chat_request', label: 'Requisicao' },
  { value: 'chat_response', label: 'Resposta' },
  { value: 'tool_call', label: 'Chamada de Tool' },
  { value: 'tool_response', label: 'Resposta de Tool' },
  { value: 'guardrail_check', label: 'Verificacao de Guardrail' },
  { value: 'guardrail_blocked', label: 'Bloqueio de Guardrail' },
  { value: 'context_build', label: 'Construcao de Contexto' },
  { value: 'error', label: 'Erro' },
  { value: 'system', label: 'Sistema' },
];

const getLevelColor = level => {
  const colors = {
    debug: 'bg-gray-100 text-gray-700',
    info: 'bg-blue-100 text-blue-700',
    warn: 'bg-yellow-100 text-yellow-700',
    error: 'bg-red-100 text-red-700',
    success: 'bg-green-100 text-green-700',
  };
  return colors[level] || 'bg-gray-100 text-gray-700';
};

const getEventIcon = eventType => {
  const icons = {
    chat_request: 'i-lucide-message-circle',
    chat_response: 'i-lucide-message-square',
    tool_call: 'i-lucide-wrench',
    tool_response: 'i-lucide-check-circle',
    guardrail_check: 'i-lucide-shield',
    guardrail_blocked: 'i-lucide-shield-alert',
    context_build: 'i-lucide-database',
    error: 'i-lucide-alert-circle',
    system: 'i-lucide-info',
  };
  return icons[eventType] || 'i-lucide-info';
};

const formatTime = isoString => {
  const date = new Date(isoString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDate = isoString => {
  const date = new Date(isoString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
};

const toggleExpand = logId => {
  if (expandedLogs.value.has(logId)) {
    expandedLogs.value.delete(logId);
  } else {
    expandedLogs.value.add(logId);
  }
};

const filteredLogs = computed(() => {
  return logs.value.filter(log => {
    if (selectedLevel.value && log.level !== selectedLevel.value) return false;
    if (selectedEventType.value && log.event_type !== selectedEventType.value)
      return false;
    return true;
  });
});

const fetchLogs = async () => {
  try {
    isLoading.value = true;
    const params = {};
    if (selectedLevel.value) params.level = selectedLevel.value;
    if (selectedEventType.value) params.event_type = selectedEventType.value;

    const response = await agentLogsAPI.getLogs(props.agentId, params);
    logs.value = response.data.logs || [];
  } catch {
    useAlert('Erro ao carregar logs');
  } finally {
    isLoading.value = false;
  }
};

const fetchStats = async () => {
  try {
    const response = await agentLogsAPI.getStats(props.agentId);
    stats.value = response.data;
  } catch {
    // Silently fail for stats
  }
};

const startStreaming = async () => {
  if (isStreaming.value) return;

  isStreaming.value = true;

  const poll = async () => {
    if (!isStreaming.value) return;

    try {
      const response = await agentLogsAPI.streamLogs(
        props.agentId,
        lastPollTime.value,
        15
      );

      if (response.data.logs?.length > 0) {
        const newLogs = response.data.logs.filter(
          newLog => !logs.value.find(l => l.id === newLog.id)
        );

        if (newLogs.length > 0) {
          logs.value = [...newLogs, ...logs.value];

          if (logs.value.length > 500) {
            logs.value = logs.value.slice(0, 500);
          }

          fetchStats();
        }
      }

      lastPollTime.value = response.data.poll_after;
    } catch {
      // Streaming error, will retry
    }

    if (isStreaming.value) {
      streamTimeout = setTimeout(poll, 1000);
    }
  };

  poll();
};

const stopStreaming = () => {
  isStreaming.value = false;
  if (streamTimeout) {
    clearTimeout(streamTimeout);
    streamTimeout = null;
  }
};

const clearLogs = async () => {
  try {
    isClearing.value = true;
    await agentLogsAPI.clearLogs(props.agentId);
    logs.value = [];
    fetchStats();
    useAlert('Logs limpos com sucesso');
    clearDialogRef.value?.close();
  } catch {
    useAlert('Erro ao limpar logs');
  } finally {
    isClearing.value = false;
  }
};

const refreshLogs = async () => {
  await fetchLogs();
  await fetchStats();
};

const handleLevelChange = value => {
  selectedLevel.value = value;
  fetchLogs();
};

const handleEventTypeChange = value => {
  selectedEventType.value = value;
  fetchLogs();
};

onMounted(async () => {
  await fetchLogs();
  await fetchStats();
  startStreaming();
});

onUnmounted(() => {
  stopStreaming();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <SettingsHeader
        heading="Logs do Agente"
        description="Visualize logs em tempo real das atividades do agente."
      />
      <div class="flex items-center gap-2">
        <Button
          :label="isStreaming ? 'Pausar' : 'Continuar'"
          :icon="isStreaming ? 'i-lucide-pause' : 'i-lucide-play'"
          variant="secondary"
          size="sm"
          @click="isStreaming ? stopStreaming() : startStreaming()"
        />
        <Button
          icon="i-lucide-refresh-cw"
          variant="secondary"
          size="sm"
          @click="refreshLogs"
        />
        <Button
          label="Limpar"
          icon="i-lucide-trash-2"
          variant="secondary"
          size="sm"
          color="ruby"
          @click="clearDialogRef?.open()"
        />
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <div class="p-4 bg-n-alpha-1 border border-n-weak rounded-xl">
        <p class="text-xs text-n-slate-10 mb-1">Total Hoje</p>
        <p class="text-2xl font-bold text-n-slate-12">
          {{ stats.total_today || 0 }}
        </p>
      </div>
      <div class="p-4 bg-green-50 border border-green-200 rounded-xl">
        <p class="text-xs text-green-600 mb-1">Sucessos</p>
        <p class="text-2xl font-bold text-green-700">
          {{ stats.success_count || 0 }}
        </p>
      </div>
      <div class="p-4 bg-red-50 border border-red-200 rounded-xl">
        <p class="text-xs text-red-600 mb-1">Erros</p>
        <p class="text-2xl font-bold text-red-700">
          {{ stats.error_count || 0 }}
        </p>
      </div>
      <div class="p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <p class="text-xs text-blue-600 mb-1">Tempo Medio</p>
        <p class="text-2xl font-bold text-blue-700">
          {{ stats.avg_execution_time ? `${stats.avg_execution_time}ms` : '-' }}
        </p>
      </div>
      <div class="p-4 bg-purple-50 border border-purple-200 rounded-xl">
        <p class="text-xs text-purple-600 mb-1">Tokens</p>
        <p class="text-2xl font-bold text-purple-700">
          {{ stats.total_tokens || 0 }}
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-4 mb-4">
      <ComboBox
        :model-value="selectedLevel"
        :options="levelOptions"
        placeholder="Todos os niveis"
        search-placeholder="Buscar..."
        @update:model-value="handleLevelChange"
      />
      <ComboBox
        :model-value="selectedEventType"
        :options="eventTypeOptions"
        placeholder="Todos os eventos"
        search-placeholder="Buscar..."
        @update:model-value="handleEventTypeChange"
      />
      <div class="flex items-center gap-2 ml-auto">
        <span
          v-if="isStreaming"
          class="flex items-center gap-2 text-sm text-green-600"
        >
          <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Ao vivo
        </span>
        <span v-else class="flex items-center gap-2 text-sm text-n-slate-10">
          <span class="w-2 h-2 rounded-full bg-gray-400" />
          Pausado
        </span>
      </div>
    </div>

    <!-- Logs List -->
    <div class="bg-n-solid-1 border border-n-weak rounded-xl overflow-hidden">
      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="filteredLogs.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <div
          class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-scroll-text text-2xl text-n-slate-10" />
        </div>
        <h4 class="text-md font-medium text-n-slate-12 mb-1">
          Nenhum log encontrado
        </h4>
        <p class="text-sm text-n-slate-11 max-w-md">
          Os logs aparecerao aqui quando o agente processar mensagens.
        </p>
      </div>

      <!-- Logs -->
      <div v-else class="max-h-[500px] overflow-y-auto">
        <div
          v-for="log in filteredLogs"
          :key="log.id"
          class="border-b border-n-weak last:border-b-0"
        >
          <!-- Log Row -->
          <div
            class="flex items-center gap-3 px-4 py-3 hover:bg-n-alpha-1 cursor-pointer transition-colors"
            @click="toggleExpand(log.id)"
          >
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
              :class="getLevelColor(log.level)"
            >
              {{ log.level.toUpperCase() }}
            </span>
            <span
              class="text-lg text-n-slate-10 shrink-0"
              :class="getEventIcon(log.event_type)"
            />
            <p class="text-sm text-n-slate-12 flex-1 truncate">
              {{ log.message }}
            </p>
            <div class="text-right shrink-0">
              <p class="text-xs text-n-slate-10">
                {{ formatTime(log.created_at) }}
              </p>
              <p class="text-xs text-n-slate-9">
                {{ formatDate(log.created_at) }}
              </p>
            </div>
            <span
              v-if="log.execution_time_ms"
              class="text-xs text-n-slate-10 shrink-0"
            >
              {{ log.execution_time_ms }}ms
            </span>
            <span
              class="i-lucide-chevron-down text-n-slate-10 transition-transform shrink-0"
              :class="{ 'rotate-180': expandedLogs.has(log.id) }"
            />
          </div>

          <!-- Expanded Details -->
          <div
            v-if="expandedLogs.has(log.id)"
            class="px-4 py-3 bg-n-alpha-1 border-t border-n-weak"
          >
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
              <div>
                <p class="text-n-slate-10">Tipo de Evento</p>
                <p class="text-n-slate-12 font-medium">{{ log.event_type }}</p>
              </div>
              <div v-if="log.conversation_id">
                <p class="text-n-slate-10">Conversa</p>
                <p class="text-n-slate-12 font-medium">
                  #{{ log.conversation_id }}
                </p>
              </div>
              <div v-if="log.session_id">
                <p class="text-n-slate-10">Sessao</p>
                <p class="text-n-slate-12 font-medium text-xs">
                  {{ log.session_id }}
                </p>
              </div>
              <div v-if="log.tokens_used">
                <p class="text-n-slate-10">Tokens</p>
                <p class="text-n-slate-12 font-medium">{{ log.tokens_used }}</p>
              </div>
            </div>

            <div class="mb-3">
              <p class="text-n-slate-10 text-sm mb-1">Mensagem Completa</p>
              <p
                class="text-n-slate-12 text-sm bg-n-alpha-2 rounded-lg p-3 break-words"
              >
                {{ log.message }}
              </p>
            </div>

            <div v-if="log.metadata && Object.keys(log.metadata).length > 0">
              <p class="text-n-slate-10 text-sm mb-1">Metadados</p>
              <pre
                class="text-xs text-n-slate-12 bg-n-alpha-2 rounded-lg p-3 overflow-x-auto"
                >{{ JSON.stringify(log.metadata, null, 2) }}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Card -->
    <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
      <div class="flex gap-3">
        <span class="i-lucide-info text-blue-600 text-xl flex-shrink-0" />
        <div>
          <h4 class="font-medium text-blue-900 mb-1">Sobre os Logs</h4>
          <p class="text-sm text-blue-700">
            Os logs mostram todas as atividades do agente em tempo real,
            incluindo requisicoes, respostas, chamadas de tools e verificacoes
            de guardrails. Use os filtros para encontrar eventos especificos.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Clear Logs Dialog -->
  <Dialog
    ref="clearDialogRef"
    type="alert"
    title="Limpar Logs"
    description="Deseja limpar todos os logs deste agente? Esta acao nao pode ser desfeita."
    confirm-button-label="Limpar Logs"
    cancel-button-label="Cancelar"
    :is-loading="isClearing"
    width="md"
    @confirm="clearLogs"
  />
</template>
